import axios from 'axios';
import { FORM_ERROR } from 'final-form';
import { application } from '../constants/configs';
import tokenProvider from 'axios-token-interceptor';
import pRetry from 'p-retry';
import { store } from '../AppRenderer';
import { loginUser, logoutUser } from '../redux/actions';
import { NotificationManager } from '../components/common/react-notifications';
import pDebounce from 'p-debounce';
import i18n from './i18n';
import { omit } from 'lodash';

export let timeOut = 30000;

function getMaxAge(res) {
  return res.expiresIn ? res.expiresIn * 1000 - 600000 : 3600000;
}

function headerFormatter(res) {
  return 'Bearer ' + res.token;
}

class Api {
  _cachedToken;
  _networkErrorsCount = 0;
  /**
   * Oauth password grant
   * @param {string} username
   * @param {string} password
   * @return {Promise<object>}
   */
  passwordGrant = (data) => {
    return pRetry(
      async () => {
        try {
          const currentLanguage = i18n.language;
          const instance = axios.create({
            method: 'post',
            url: application.apiUrl + '/v2/admin-ui/auth',
            headers: {
              'Accept-Language': `${currentLanguage}`,
            },
            data,
            cancelToken: new axios.CancelToken(function executor(cancel) {
              setTimeout(() => {
                cancel();
              }, timeOut);
            }),
            withCredentials: true,
          });

          const response = await instance.request();

          if (response.data) {
            this._cachedToken = tokenProvider.tokenCache(
              () => Promise.resolve(response.data),
              {
                getMaxAge,
              }
            );
            await localStorage.setItem('token', response.data.token);
            await localStorage.setItem('username', data.username);
            store.dispatch(loginUser());
          }
        } catch (e) {
          if (
            e.response &&
            Math.floor(e.response.status / 100) === 4 &&
            e.response.status !== 404
          ) {
            if (
              e.response.data &&
              e.response.data.code !== 'GOOGLE_AUTH_TOKEN_REQUIRED' &&
              e.response.data.message
            ) {
              NotificationManager.error(
                e.response.data.message,
                i18n.t('Error!')
              );
            }
            throw new pRetry.AbortError(e);
          }
          if (!e.response && e.request && !e.request.response) {
            throw new pRetry.AbortError(e);
          }
          if (!e.request) {
            throw new pRetry.AbortError(e);
          }
          throw e;
        }
      },
      {
        retries: 3,
        factor: 2.4477,
      }
    );
  };

  logOut = async () => {
    this._cachedToken = undefined;
    await localStorage.removeItem('token');
    store.dispatch(logoutUser());
  };

  /**
   * Oauth refresh token
   * @return {Promise<object>}
   */
  refreshToken = () => {
    return pRetry(
      async () => {
        const token = await localStorage.getItem('token');
        if (!token) {
          const e = new Error(i18n.t('Token not found'));
          e.suppressEmitNotification = true;
          throw new pRetry.AbortError(e);
        }

        try {
          const currentLanguage = i18n.language;
          const instance = axios.create({
            method: 'post',
            url: application.apiUrl + '/v2/admin-ui/refreshJWT',
            headers: {
              'Accept-Language': `${currentLanguage}`,
              'Cache-Control': 'no-cache',
            },
            cancelToken: new axios.CancelToken(function executor(cancel) {
              setTimeout(() => {
                cancel();
              }, timeOut);
            }),
            withCredentials: true,
          });

          instance.interceptors.request.use(
            tokenProvider({
              token,
            })
          );

          const response = await instance.request();

          if (response.data) {
            await localStorage.setItem('token', response.data.token);
            return response.data;
          }
        } catch (e) {
          if (
            e.response &&
            Math.floor(e.response.status / 100) === 4 &&
            e.response.status !== 404
          ) {
            await this.logOut();
            throw new pRetry.AbortError(e);
          }
          if (e.response && Math.floor(e.response.status / 100) === 5) {
            throw new pRetry.AbortError(e);
          }
          if (!e.response && e.request && !e.request.response) {
            throw new pRetry.AbortError(e);
          }
          if (!e.request) {
            throw new pRetry.AbortError(e);
          }
          throw e;
        }
      },
      {
        retries: 3,
        factor: 2.4477,
      }
    );
  };

  /**
   * @param {string} method
   * @param {string} path
   * @param {string|object|ArrayBuffer|ArrayBufferView|URLSearchParams} data
   * @param {object} options
   * @param {boolean} authenticate
   * @return {Promise<{data:*,status:number,headers:object,config:object,request:XMLHttpRequest}>}
   * @private
   */
  _invoke = async (method, path, data, options, authenticate) => {
    const currentLanguage = i18n.language;
    const instance = axios.create({
      method,
      url: application.apiUrl + path,
      headers: {
        'Cache-Control': 'no-cache',
        'Accept-Language': `${currentLanguage}`,
        ...(options && options.headers ? options.headers : null),
      },
      params: data ? data.params : null,
      data,
      ...(data instanceof FormData
        ? {
            cancelToken: null,
            timeout: 0,
          }
        : {
            cancelToken: new axios.CancelToken(function executor(cancel) {
              setTimeout(() => {
                cancel();
              }, timeOut);
            }),
          }),
      ...omit(options, 'headers'),
      withCredentials: true,
    });

    if (authenticate) {
      if (!this._cachedToken) {
        this._cachedToken = tokenProvider.tokenCache(this.refreshToken, {
          getMaxAge,
        });
      }
      instance.interceptors.request.use(
        tokenProvider({
          getToken: this._cachedToken,
          headerFormatter,
        })
      );
    }

    try {
      return await instance.request();
    } catch (e) {
      if (authenticate && e.response && e.response.status === 401) {
        await this.logOut();
      } else if (!e.request) {
        throw new pRetry.AbortError(e);
      }
      throw e;
    }
  };

  /**
   * Invoke API method
   * @param {string} method
   * @param {string} path
   * @param {string|object|ArrayBuffer|ArrayBufferView|URLSearchParams} data
   * @param {object} options
   * @return {Promise<{data:*,status:number,headers:object,config:object,request:XMLHttpRequest}>}
   */
  invoke = (method, path, data, options) => {
    if (!path.includes('/v1/') && !path.includes('/v2/')) {
      path = '/v1' + path;
    }
    const apiOptions = {
      displayError: true,
      ...((options && options.api) || {}),
    };

    if (options) {
      delete options.api;
    }

    return pRetry(
      async () => {
        try {
          return await this._invoke(
            method,
            path,
            data,
            options,
            apiOptions.authenticate !== false
          );
        } catch (e) {
          if (e.response && Math.floor(e.response.status / 100) === 5) {
            throw new pRetry.AbortError(e);
          }
          if (e.response && Math.floor(e.response.status / 100) === 4) {
            throw new pRetry.AbortError(e);
          }
          if (!e.response && e.request && !e.request.response) {
            throw new pRetry.AbortError(e);
          }
          throw e;
        }
      },
      {
        retries: method.toUpperCase() === 'GET' ? 3 : 0,
        factor: 2.4477,
      }
    ).catch((e) => {
      let message;
      if (e.response) {
        if (e.response.data) {
          if (e.response.data.details) {
            e.fieldError = e.response.data.details.reduce((obj, item) => {
              const field = item.field;
              // eslint-disable-next-line no-prototype-builtins
              if (!obj.hasOwnProperty(field)) {
                obj[field] = item.issue;
              }
              return obj;
            }, {});
          }
          if (e.response.data.message) {
            message = e.response.data.message;
          } else {
            if (e.response.status === 413) {
              message = i18n.t('File size is too large to upload');
            }
          }
        }
      } else if (e.request) {
        e.isNetworkError = true;
        this._networkErrorsCount += 1;
        message = i18n.t('Check your internet connection and try again');
      } else {
        message =
          process.env.NODE_ENV === 'development'
            ? e.message
            : i18n.t('An error occurred');
      }

      if (message) {
        e.message = message;
        e.fieldError = e.fieldError
          ? { ...e.fieldError, [FORM_ERROR]: message }
          : { [FORM_ERROR]: message };
        if (apiOptions.displayError) {
          if (!e.isNetworkError) {
            if (typeof message === 'string') {
              NotificationManager.error(message, i18n.t('Error!'));
            }
          } else {
            pDebounce(() => {
              if (this._networkErrorsCount >= 3) {
                NotificationManager.error(message, i18n.t('Error!'));
              }
              this._networkErrorsCount = 0;
            }, 10000);
          }
        }
      }
      throw e;
    });
  };
}

export default new Api();
